import type { StripeError } from "@stripe/stripe-js";

import { t } from "@/i18n-js/instance";
import type { PaywallCheckoutError } from "@/react/api/paywallCheckoutApi";
import { ApiError } from "@/react/config/CustomErrors";
import { type PaywallFailure, isPaywallFailure } from "@/react/types";

const isApiError = (error: unknown): error is ApiError =>
  error instanceof ApiError;

// Not the best way to identify the error type, but it's the best we can do given these are only
// types, not instances of custom error classes.
const isStripeError = (error: unknown): error is StripeError =>
  Boolean(
    error &&
    typeof error === "object" &&
    "type" in error &&
    typeof error.type === "string",
  );

type LegacyPaywallError = PaywallCheckoutError;

const isLegacyPaywallError = (error: unknown): error is LegacyPaywallError =>
  Boolean(
    error &&
    typeof error === "object" &&
    "error_details" in error &&
    error.error_details,
  );

interface NewPaywallError {
  failure: PaywallFailure;
}
const isNewPaywallError = (error: unknown): error is NewPaywallError =>
  Boolean(
    error &&
    typeof error === "object" &&
    "failure" in error &&
    typeof error.failure === "object" &&
    isPaywallFailure(error),
  );

const checkoutErrorTypes = {
  stripeError: "stripeError",
  paywallError: "paywallError",
  apiError: "apiError",
  unknownError: "unknownError",
} as const;
type CheckoutErrorType =
  (typeof checkoutErrorTypes)[keyof typeof checkoutErrorTypes];

export interface CheckoutErrorOptions {
  cause?: unknown;
  context?: StripeError | LegacyPaywallError | NewPaywallError;
  shouldAllowToResubmit?: boolean;
  type: CheckoutErrorType;
}

export class CheckoutError extends Error {
  caller: string;
  type: CheckoutErrorType;
  context?: CheckoutErrorOptions["context"];
  shouldAllowToResubmit: CheckoutErrorOptions["shouldAllowToResubmit"];

  constructor(
    caller: string,
    message: string,
    { type, cause, context, shouldAllowToResubmit }: CheckoutErrorOptions = {
      type: checkoutErrorTypes.unknownError,
    },
  ) {
    super(message, { cause });
    this.caller = caller;
    this.context = context;
    this.shouldAllowToResubmit = shouldAllowToResubmit ?? true;
    this.type = type;
  }

  getExtra(): Record<string, unknown> {
    if (this.isStripeError()) {
      return {
        checkout_error_type: this.type,
        stripe_error_type: this.context.type,
        stripe_error_code: this.context.code,
        stripe_error_decline_code: this.context.decline_code,
      };
    }
    if (this.isNewPaywallError()) {
      return {
        checkout_error_type: this.type,
        paywall_failure_category: this.context.failure.category,
        paywall_failure_code: this.context.failure.code,
        paywall_failure_level: this.context.failure.level,
        paywall_failure_provider_code: this.context.failure.metadata?.code,
      };
    }
    if (this.isLegacyPaywallError()) {
      return {
        checkout_error_type: this.type,
        legacy_paywall_error_details: this.context.error_details,
      };
    }
    return { checkout_error_type: this.type };
  }

  isStripeError(): this is CheckoutError & { context: StripeError } {
    return isStripeError(this.context);
  }

  isLegacyPaywallError(): this is CheckoutError & {
    context: LegacyPaywallError;
  } {
    return isLegacyPaywallError(this.context);
  }

  isNewPaywallError(): this is CheckoutError & { context: NewPaywallError } {
    return isNewPaywallError(this.context);
  }

  static generic(caller: string): CheckoutError {
    return new CheckoutError(
      caller,
      t("services.paywalls.failures.internal.generic"),
    );
  }

  static stripeNotInitialized(caller: string): CheckoutError {
    return new CheckoutError(
      caller,
      t("services.paywalls.failures.stripe.not_initialized"),
    );
  }

  static fromStripeError(caller: string, error: StripeError): CheckoutError {
    let message = error.message;
    if (!message) {
      message = t("services.paywalls.failures.internal.generic");
    }
    return new CheckoutError(caller, message, {
      context: error,
      type: checkoutErrorTypes.stripeError,
    });
  }

  static fromPaywallError(
    caller: string,
    error: NewPaywallError | LegacyPaywallError,
  ): CheckoutError {
    if (isNewPaywallError(error)) {
      return new CheckoutError(caller, error.failure.message, {
        context: error,
        type: checkoutErrorTypes.paywallError,
      });
    }
    return new CheckoutError(caller, error.message, {
      context: error,
      type: checkoutErrorTypes.paywallError,
    });
  }

  static fromApiError(caller: string, error: ApiError): CheckoutError {
    if (isLegacyPaywallError(error.body) || isNewPaywallError(error.body)) {
      return CheckoutError.fromPaywallError(caller, error.body);
    }
    return new CheckoutError(caller, error.message, {
      cause: error,
      type: checkoutErrorTypes.apiError,
    });
  }

  static fromUnknownError(caller: string, error: unknown): CheckoutError {
    if (error instanceof CheckoutError) {
      return error;
    }
    if (isApiError(error)) {
      return CheckoutError.fromApiError(caller, error);
    }
    if (isStripeError(error)) {
      return CheckoutError.fromStripeError(caller, error);
    }
    if (isNewPaywallError(error) || isLegacyPaywallError(error)) {
      return CheckoutError.fromPaywallError(caller, error);
    }
    if (error instanceof Error) {
      return new CheckoutError(caller, error.message, {
        cause: error,
        type: checkoutErrorTypes.unknownError,
      });
    }
    return CheckoutError.generic(caller);
  }
}
